import React, {useState} from 'react';
import {iconForCapability} from './supervisorLiveTrace';
import type {SupervisorDetailedFinding} from './supervisorChatApi';
import {
  ICON_COLORS,
  QueriesPanel,
  QueriesToggle,
  QueriesBody,
  QueryBlockWrap,
  QueryHeaderRow,
  QueryDot,
  CodeBlockPre,
  EndSourcesRow,
  SourcePill,
} from './SupervisorAgentTrace.style';

const ChevronSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.4}>
    <path d='M9 6l6 6-6 6' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

// `summary_md` often bundles the SQL alongside the result rows/table — keep
// only the query: a fenced ```sql block if present, otherwise everything
// before the first "Result(s):"/table marker.
const extractSqlOnly = (summaryMd?: string): string => {
  if (!summaryMd) return 'No output.';
  const fenced = summaryMd.match(/```sql\s*([\s\S]*?)```/i) || summaryMd.match(/```\s*([\s\S]*?)```/);
  if (fenced) return fenced[1].trim();
  const resultMarkerIdx = summaryMd.search(/\n\s*(result|results|output)s?\s*:|\n\s*\|/i);
  return (resultMarkerIdx > -1 ? summaryMd.slice(0, resultMarkerIdx) : summaryMd).trim();
};

const looksLikeSql = (text: string): boolean => /\b(select|insert\s+into|update|delete\s+from|with)\b/i.test(text);

const SQL_CLAUSE_KEYWORDS = [
  'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT',
  'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'INNER JOIN', 'JOIN',
  'UNION ALL', 'UNION', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM',
];

// The API returns SQL as one unbroken line — pretty-print it (one
// clause/column per line) to match the mockup's multi-line code block, since
// there's no SQL-formatting library in this project.
const formatSql = (sql: string): string => {
  let text = sql.replace(/\s+/g, ' ').trim();

  text = text.replace(/\s*\bSELECT\b\s*/i, '\nSELECT\n  ');
  SQL_CLAUSE_KEYWORDS.forEach((keyword) => {
    const pattern = new RegExp(`\\s+(${keyword.replace(/ /g, '\\s+')})\\b`, 'gi');
    text = text.replace(pattern, '\n$1');
  });
  // One column/value per line, skipping commas inside parentheses.
  text = text.replace(/,\s*(?![^(]*\))/g, ',\n  ');
  text = text.replace(/\s+(AND|OR)\s+/gi, '\n  $1 ');

  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .join('\n')
    .trim();
};

interface AgentOutputsPanelProps {
  findings?: SupervisorDetailedFinding[];
  sources?: string[];
}

// Real-data counterpart to the mockup's "View queries & agent outputs"
// panel — the real backend doesn't return literal SQL/Cypher, so each block
// shows a node's own `summary_md` instead.
export const AgentOutputsPanel: React.FC<AgentOutputsPanelProps> = ({findings, sources}) => {
  const [open, setOpen] = useState(false);

  if (!findings || findings.length === 0) return null;

  return (
    <QueriesPanel $visible>
      <QueriesToggle
        $open={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <ChevronSvg />
        <span>View queries ({findings.length})</span>
      </QueriesToggle>
      <QueriesBody $open={open}>
        {findings.map((finding, i) => {
          const rawQuery = extractSqlOnly(finding.summary_md);
          const displayQuery = looksLikeSql(rawQuery) ? formatSql(rawQuery) : rawQuery;
          return (
            <QueryBlockWrap key={finding.node_id || i}>
              <QueryHeaderRow>
                <QueryDot $color={ICON_COLORS[iconForCapability(finding.capability_id)]} />
                {finding.capability_id || finding.node_id || `Agent ${i + 1}`}
              </QueryHeaderRow>
              <CodeBlockPre>
                <code>{displayQuery}</code>
              </CodeBlockPre>
            </QueryBlockWrap>
          );
        })}
        {/* {sources && sources.length > 0 && (
          <EndSourcesRow>
            {sources.map((source) => (
              <SourcePill key={source}>{source}</SourcePill>
            ))}
          </EndSourcesRow>
        )} */}
      </QueriesBody>
    </QueriesPanel>
  );
};

export default AgentOutputsPanel;
